import React, { useEffect, useState } from "react";
import { api, fmtDateTime, fmtMoney } from "@/lib/api";
import { toast } from "sonner";

const NUMERIC_FIELDS = [
  "brokerage_per_executed_order",
  "stt_sell_rate",
  "exchange_rate",
  "ipft_rate",
  "sebi_rate",
  "stamp_buy_rate",
  "gst_rate",
];

function FeeScheduleEditor() {
  const [rows, setRows] = useState([]);
  const [exchange, setExchange] = useState("NSE");
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/costs/schedules");
      setRows(data.schedules || []);
      // Seed the draft with the currently active schedule for the selected exchange.
      const { data: active } = await api.get("/costs/schedules/active", {
        params: { exchange },
      });
      setDraft({
        exchange,
        effective_from: new Date().toISOString().slice(0, 10),
        ...active.schedule,
        note: "",
      });
    } catch (e) {
      toast.error("Failed to load fee schedules");
    }
  };

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchange]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    try {
      const payload = { ...draft };
      for (const f of NUMERIC_FIELDS) payload[f] = Number(payload[f]);
      await api.put("/costs/schedules", payload);
      toast.success(`${draft.exchange} schedule saved (effective ${draft.effective_from})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="surface rounded-sm p-4" data-testid="fee-schedule-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Fee Schedule Editor</div>
        <div className="flex gap-1" data-testid="fee-schedule-exchange-switch">
          {["NSE", "BSE"].map((ex) => (
            <button
              key={ex}
              data-testid={`fee-exchange-${ex}`}
              onClick={() => setExchange(ex)}
              className={`px-2 py-1 text-[10px] uppercase tracking-[0.15em] rounded-sm border ${
                exchange === ex
                  ? "border-blue-500 bg-blue-500/20 text-blue-300"
                  : "border-slate-700 text-slate-400 hover:bg-slate-800/50"
              }`}
            >
              {ex}
            </button>
          ))}
        </div>
      </div>
      {draft && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
              Effective from
            </span>
            <input
              type="date"
              data-testid="fee-effective-from"
              className="w-full mt-1 bg-slate-900/50 border border-slate-700 text-xs font-mono px-2 py-1 rounded-sm"
              value={draft.effective_from}
              onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })}
            />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Note</span>
            <input
              type="text"
              data-testid="fee-note"
              placeholder="e.g. Zerodha revision 2026-05"
              className="w-full mt-1 bg-slate-900/50 border border-slate-700 text-xs font-mono px-2 py-1 rounded-sm"
              value={draft.note || ""}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
            />
          </label>
          {NUMERIC_FIELDS.map((f) => (
            <label key={f} className="block">
              <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-mono">
                {f}
              </span>
              <input
                type="number"
                step="any"
                data-testid={`fee-${f}`}
                className="w-full mt-1 bg-slate-900/50 border border-slate-700 text-xs font-mono px-2 py-1 rounded-sm"
                value={draft[f] ?? ""}
                onChange={(e) => setDraft({ ...draft, [f]: e.target.value })}
              />
            </label>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between">
        <div className="text-[10px] text-slate-500 leading-relaxed">
          Rates are stored per <span className="font-mono">(exchange, effective_from)</span>. Values must be fractions (e.g. <span className="font-mono">0.0015</span> for 0.15%). GST is stored as <span className="font-mono">0.18</span> for 18%.
        </div>
        <button
          data-testid="fee-schedule-save"
          disabled={busy}
          onClick={save}
          className="px-4 py-2 border border-blue-500 bg-blue-500/20 text-blue-300 text-xs uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save schedule"}
        </button>
      </div>

      {rows.length > 0 && (
        <div className="mt-4 overflow-x-auto" data-testid="fee-schedule-history">
          <table className="dt">
            <thead>
              <tr>
                <th>Exchange</th>
                <th>Effective</th>
                <th className="num">Brokerage</th>
                <th className="num">STT Sell</th>
                <th className="num">Exch</th>
                <th className="num">SEBI</th>
                <th className="num">Stamp</th>
                <th className="num">GST</th>
                <th>Source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={`${r.exchange}-${r.effective_from}-${i}`} data-testid={`fee-row-${r.exchange}-${r.effective_from}`}>
                  <td className="font-mono">{r.exchange}</td>
                  <td className="font-mono text-slate-400">{r.effective_from}</td>
                  <td className="num font-mono">{r.brokerage_per_executed_order}</td>
                  <td className="num font-mono">{r.stt_sell_rate}</td>
                  <td className="num font-mono">{r.exchange_rate}</td>
                  <td className="num font-mono">{r.sebi_rate}</td>
                  <td className="num font-mono">{r.stamp_buy_rate}</td>
                  <td className="num font-mono">{r.gst_rate}</td>
                  <td className="text-[10px] uppercase text-slate-500">
                    {r.source === "builtin_default" ? "BUILTIN" : (r.actor ? "USER" : "DB")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function Costs() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/costs").then(({ data }) => setData(data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="costs-page">
      <h1 className="text-2xl font-medium tracking-tight">Costs</h1>
      <p className="text-xs text-slate-500 uppercase tracking-[0.15em]">
        Kite virtual contract note estimate vs versioned fallback
      </p>

      <FeeScheduleEditor />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="surface rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Estimate</div>
          <div className="text-2xl font-mono mt-1">{fmtMoney(data?.total_estimate)}</div>
        </div>
        <div className="surface rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total Reconciled</div>
          <div className="text-2xl font-mono mt-1">{fmtMoney(data?.total_reconciled)}</div>
        </div>
        <div className="surface rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Note</div>
          <div className="text-xs text-slate-400 mt-2 leading-relaxed">
            All costs labelled <span className="text-amber-400 font-mono">VERSIONED_FALLBACK</span> until a live Kite connection is used to fetch the virtual contract note via <span className="font-mono">/charges/orders</span>.
          </div>
        </div>
      </div>

      <div className="surface rounded-sm overflow-x-auto">
        <table className="dt">
          <thead>
            <tr>
              <th>Closed</th>
              <th>Symbol</th>
              <th className="num">Entry Chg</th>
              <th className="num">Exit Chg</th>
              <th className="num">Total Estimate</th>
              <th className="num">Reconciled</th>
              <th className="num">Variance</th>
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.trade_id} data-testid="cost-row">
                <td className="font-mono text-slate-400">{fmtDateTime(r.closed_at)}</td>
                <td className="font-mono">{r.tradingsymbol}</td>
                <td className="num font-mono">{fmtMoney(r.entry_charges)}</td>
                <td className="num font-mono">{fmtMoney(r.exit_charges)}</td>
                <td className="num font-mono">{fmtMoney(r.total_charges)}</td>
                <td className="num font-mono">{fmtMoney(r.reconciled_total)}</td>
                <td className="num font-mono">{fmtMoney(r.variance)}</td>
                <td className="text-[10px] uppercase text-slate-500">{r.source === "KITE_VIRTUAL_CONTRACT_NOTE" ? "KITE" : "FALLBACK"}</td>
              </tr>
            ))}
            {(!data?.rows || data.rows.length === 0) && (
              <tr>
                <td colSpan="8" className="text-center text-slate-500 py-8">No cost records.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
